import {
	ArrowBack as ArrowBackIcon,
	Bookmark as BookmarkIcon,
	BookmarkBorder as BookmarkBorderIcon,
	Download as DownloadIcon,
	InfoOutlined as InfoOutlinedIcon,
	Tune as TuneIcon
} from "@mui/icons-material";
import { Badge, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { isEqual } from "lodash";
import moment from "moment";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { AGDataGrid } from "../../components/AGDataGrid";
import PlateImage from "../../components/PlateImage";
import TableFilterDropdown, { ITableFilterDropdownMenuItem } from "../../components/TableFilterDropdown";
import TableSearchInput from "../../components/TableSearchInput";
import TableSettingsDialog, { ColumnVisibilityType } from "../../components/TableSettingsDialog";
import { useAppSelector } from "../../redux";
import {
	useEditEventBookmarkMutation,
	useGetAIEventsQuery,
	useGetAlertEventsQuery
} from "../../redux/reducers/alerts.reducer";
import {
	useGetAllCamerasListQuery,
	useGetUserSettingsQuery,
	useGetVehicleCategoriesQuery,
	useGetVehicleTypesListQuery,
	useGetViolationsListQuery,
	useUpdateUserSettingsMutation
} from "../../redux/reducers/settings.reducer";
import { useDownloadJob } from "../../hooks/useDownloadReport";
import {
	EVENT_DIRECTION_E,
	EVENT_PRODUCT_TYPE_E,
	EventChallanStatusType,
	EventValidationStatusType,
	IEvidenceImageURLDetails,
	IGetAIEventsRequestData,
	IGetAlertEventsRequestData,
	IGetAlertEventsResponseData,
	IVehicleTypeDetails
} from "../../types";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import { DASHBOARD_EVENTS_REFRESH_INTERVAL, NON_HSRP_VIOLATION_NAMES, ROWS_PER_PAGE } from "../../utils/constants";
import AISearchSettingsDialog, { IAISearchSettingsInputs } from "./AISearchSettingsDialog";
import "./index.css";
import VehicleLogDetailsDialog from "./VehicleLogDetailsDialog";
import { useNotification } from "../../components/FeedbackNotification/useNotification";

export enum ENFORCEMENT_VERIFY_STATUS_E {
	REJECT = 0,
	PENDING = 1,
	VERIFIED = 2,
	CHALLAN_FAILED = 3,
	CHALLAN_ISSUED = 4
}

interface ILogsProps {
	sessionId: number;
	isLive?: boolean;
	navigateBack?: () => void;
}

export interface IVehicleLogViolationDetails {
	id: number;
	name: string;
	lane_id: number;
	lane: string;
	violation_id: number;
	is_speed_related: boolean;
}

export interface IVehicleLogRowDetails {
	id: number;
	timestamp: string;
	number_plate: string;
	location: string;
	speed_radar: string;
	speed_vision: string;
	plate_type: string;
	color: string;
	color_icon: string;
	make: string;
	make_icon: string;
	model: string;
	camera: string;
	class: string;
	vehicle_class_icon: string;
	device: string;
	violations: IVehicleLogViolationDetails[];
	event_id: string;
	vehicle_type: number;
	vehicle_type_icon: string;
	direction: number;
	direction_name: string;
	is_bookmarked: boolean;
	ai_meta_data: string;
	bbox: [number, number, number, number][];
	icon: string | number | null;
	vehicle_type_name: string;
	vehicle_number: string;
	plate_image_uuid: string;
	vehicle_images: IEvidenceImageURLDetails[];
	vehicle_image_frame_id: string;
	site_id: number;
	site_name: string;
	brand_id: number;
	brand: string;
	brand_icon: string;
	model_id: number;
	color_id: number;
	speed: string;
	radar_speed: string;
	vehicle_class_id: number;
	vehicle_class: string;
	category_id: number;
	category: string;
	officer: string;
	location_coordinates: string;
	location_name: string;
	crop_coordinates: {
		plate: number[];
		violation: number[];
		plate_image_name?: string;
		violation_image_name?: string;
	};
	violation_image: string;
	status: EventValidationStatusType;
	lanes: { id: number; name: string }[];
	image_width: number;
	image_height: number;
	remarks: string;
	is_challan_created: boolean;
	retry_challan: boolean;
	challan_status: Extract<EventChallanStatusType, "failed" | "issued"> | "pending";
}

interface ISpeedRange {
	key: string;
	label: string;
}

const disabledColumns = ["date", "time", "number_plate", "icon", "vehicle_type"];

const SPEED_RANGE_LIST: ISpeedRange[] = [
	{ key: "0-20", label: "Less than 20" },
	{ key: "21-40", label: "21 to 40" },
	{ key: "41-80", label: "41 to 80" },
	{ key: "81-150", label: "81 to 150" },
	{ key: "151-", label: "150 +" }
];

const AI_TOP_K_LIST: number[] = [15, 50, 100, 150, 200, 250];

const statusMapping: Partial<Record<ENFORCEMENT_VERIFY_STATUS_E, EventValidationStatusType>> = {
	[ENFORCEMENT_VERIFY_STATUS_E.REJECT]: "reject",
	[ENFORCEMENT_VERIFY_STATUS_E.PENDING]: "pending",
	[ENFORCEMENT_VERIFY_STATUS_E.VERIFIED]: "approved"
};

/* const CustomAutocompletePopper = styled(Popper)(({ theme }) => ({
	border: "1px solid #e1e4e8",
	boxShadow: "0 8px 24px rgba(149, 157, 165, 0.2)",
	borderTopLeftRadius: 6,
	borderTopRightRadius: 6,
	width: 300,
	zIndex: theme.zIndex.modal,
	backgroundColor: "#fff"
})); */

/* const CustomAutocompleteInput = styled(InputBase)(({ theme }) => ({
	padding: 10,
	width: "100%",
	borderBottom: "1px solid #eaecef",
	"& input": {
		borderRadius: 4,
		backgroundColor: "#fff",
		padding: 8,
		border: "1px solid #eaecef",
		fontSize: 14,
		"&:focus": {
			border: `2px solid ${theme.palette.primary.main}`
		}
	}
})); */

const VehicleLogs: FC<ILogsProps> = (props) => {
	const { sessionId, isLive, navigateBack } = props;

	const authState = useAppSelector((state) => state.auth);
	const filtersState = useAppSelector((state) => state.filters);

	const { notify } = useNotification();

	const pageChanging = useRef<"next" | "previous" | null>(null);
	// const vehicleModelDropdownAnchorRef = useRef<HTMLDivElement>(null);

	const [filters, setFilters] = useState<IGetAlertEventsRequestData>({
		// timestamp__gte: "",
		// timestamp__lte: "",
		page: 1,
		product_type: EVENT_PRODUCT_TYPE_E.RECON,
		session: sessionId
	});
	const [aiSearchFilters, setAiSearchFilters] = useState<IGetAIEventsRequestData>({
		// timestamp__gte: "",
		// timestamp__lte: "",
		page_number: 1,
		top_k: AI_TOP_K_LIST[0],
		search: "",
		accuracy: 0.6,
		n_probe: 100,
		session: sessionId
	});
	const [showTableSettingsDialog, setShowTableSettingsDialog] = useState<boolean>(false);
	const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityType>({});
	const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
	const [selectedAlertDetails, setSelectedAlertDetails] = useState<IVehicleLogRowDetails | null>(null);
	const { isLoading: showDownloadButtonLoader, startDownload } = useDownloadJob();
	// const [showTableFiltersDialog, setShowTableFiltersDialog] = useState<boolean>(false);

	// const [vehicleModelDropdownOpen, setVehicleModelDropdownOpen] = useState<boolean>(false);
	// const [getVehicleModelsFilters, setGetVehicleModelsFilters] = useState<IGetVehicleModelsRequestData>({ search: "" });

	const [rowsData, setRowsData] = useState<IVehicleLogRowDetails[]>([]);
	const [showBookmarkLoaders, setShowBookmarkLoaders] = useState<string[]>([]);
	const [showAISearchSettingsDialog, setShowAISearchSettingsDialog] = useState<boolean>(false);

	// APIS
	// GET VEHICLE CATEGORIES
	const { data: getVehicleCategoriesResponse } = useGetVehicleCategoriesQuery();

	// GET VEHICLE COLORS
	// const { data: getVehicleColorsResponse } = useGetVehicleColorsQuery();

	// GET USER SETTINGS
	const { data: getUserSettingsResponse, refetch: refetchUserSettings } = useGetUserSettingsQuery();

	// UPDATE USER SETTINGS
	const [
		updateUserSettings,
		{
			isLoading: updateUserSettingsLoading,
			error: updateUserSettingsError,
			isSuccess: updateUserSettingsSuccess,
			reset: resetUpdateUserSettings
		}
	] = useUpdateUserSettingsMutation();

	// GET ALERT EVENTS
	const {
		data: getAlertEventsResponse,
		isLoading: getAlertEventsLoading,
		isFetching: getAlertEventsFetching,
		refetch: refetchAlertEvents
	} = useGetAlertEventsQuery(filters, {
		pollingInterval: isFilterApplied || !isLive ? 0 : DASHBOARD_EVENTS_REFRESH_INTERVAL * 1000,
		skip:
			!authState.user.token ||
			authState.user.token === "invalid" ||
			// !filters.timestamp__gte ||
			// !filters.timestamp__lte ||
			!!filters.ai_meta_data,
		refetchOnMountOrArgChange: true
	});

	// GET AI EVENTS
	const {
		data: getAIEventsResponse,
		isLoading: getAIEventsLoading,
		isFetching: getAIEventsFetching
	} = useGetAIEventsQuery(aiSearchFilters, {
		skip:
			!authState.user.token ||
			authState.user.token === "invalid" ||
			// !aiSearchFilters.timestamp__gte ||
			// !aiSearchFilters.timestamp__lte ||
			!aiSearchFilters.search,
		refetchOnMountOrArgChange: true
	});

	// GET VEHICLE BRANDS LIST
	// const { data: getVehicleBrandsResponse } = useGetVehicleBrandsListQuery();

	// GET VIOLATIONS LIST
	const { data: getViolationsListResponse } = useGetViolationsListQuery();

	// GET VEHICLE TYPES LIST
	const { data: getVehicleTypesListResponse } = useGetVehicleTypesListQuery();

	// GET VEHICLE MODELS LIST
	/* const {
		data: getVehicleModelsListResponse,
		isLoading: getVehicleModelsLoading,
		isFetching: getVehicleModelsFetching
	} = useGetVehicleModelsListQuery(getVehicleModelsFilters); */

	// GET VEHICLE CLASSES LIST
	// const { data: getVehicleClassesListResponse } = useGetVehicleClassesListQuery();

	// GET ALL CAMERAS LIST
	const { data: getAllCamerasListResponse } = useGetAllCamerasListQuery({ product_type: EVENT_PRODUCT_TYPE_E.RECON });

	// EDIT EVENT BOOKMARK
	const [editEventBookmark] = useEditEventBookmarkMutation();

	const vehicleCategoriesList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
		if (getVehicleCategoriesResponse) {
			return getVehicleCategoriesResponse.map((vehicleCategoryItem) => ({
				id: vehicleCategoryItem.id,
				label: vehicleCategoryItem.name?.replace(/_/g, " ")
			}));
		}

		return [];
	}, [getVehicleCategoriesResponse]);

	/* const vehicleColorsList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
		if (getVehicleColorsResponse) {
			return getVehicleColorsResponse.map((vehicleColorItem) => ({
				id: vehicleColorItem.id,
				label: vehicleColorItem.name
			}));
		}

		return [];
	}, [getVehicleColorsResponse]); */

	const columnVisibilityResponse = useMemo<{ [column_name: string]: boolean }>(() => {
		if (getUserSettingsResponse) {
			return getUserSettingsResponse.user_settings.logs;
		}

		return {};
	}, [getUserSettingsResponse]);

	const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
		const model: GridColumnVisibilityModel = {};

		for (const [columnName, visibilityDetails] of Object.entries(columnVisibility)) {
			model[columnName] = visibilityDetails.visible;
		}

		return model;
	}, [columnVisibility]);

	/* const vehicleBrandsList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
		if (getVehicleBrandsResponse) {
			return getVehicleBrandsResponse.map((vehicleBrandItem) => ({
				id: vehicleBrandItem.id,
				label: vehicleBrandItem.name
			}));
		}

		return [];
	}, [getVehicleBrandsResponse]); */

	const violationsList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
		if (getViolationsListResponse) {
			return getViolationsListResponse
				.filter(
					(violationItem) =>
						typeof violationItem.product_type !== "number" || violationItem.product_type === EVENT_PRODUCT_TYPE_E.RECON
				)
				.map((violationItem) => ({
					id: violationItem.id,
					label: NON_HSRP_VIOLATION_NAMES.includes(violationItem.name)
						? "Non HSRP"
						: convertSnakeToTitleCase(
								violationItem.name === "UNDER_SPEEDING" ? "SLOW_MOVING_VEHICLE" : violationItem.name
						  )
				}));
		}

		return [];
	}, [getViolationsListResponse]);

	const vehicleTypesList = useMemo<IVehicleTypeDetails[]>(
		() => getVehicleTypesListResponse ?? [],
		[getVehicleTypesListResponse]
	);

	/* const vehicleModelsList = useMemo<IVehicleModelDetails[]>(
		() => getVehicleModelsListResponse ?? [],
		[getVehicleModelsListResponse]
	); */

	// const vehicleClassesList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
	// 	if (getVehicleClassesListResponse) {
	// 		return getVehicleClassesListResponse.map((vehicleClassItem) => ({
	// 			id: vehicleClassItem.id,
	// 			label: vehicleClassItem.name.replace(/_/g, " ")
	// 		}));
	// 	}

	// 	return [];
	// }, [getVehicleClassesListResponse]);

	const camerasList = useMemo<ITableFilterDropdownMenuItem[]>(() => {
		if (getAllCamerasListResponse) {
			return getAllCamerasListResponse.map((cameraItem) => ({
				id: cameraItem.id,
				label: cameraItem.site_name
			}));
		}

		return [];
	}, [getAllCamerasListResponse]);

	const detailsPreviousButtonDisabled = useMemo<boolean>(() => {
		if (selectedAlertDetails && typeof filters.page === "number" && filters.page === 1) {
			const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.id === selectedAlertDetails.id);

			if (selectedAlertIndex === 0) {
				return true;
			}
		}

		return false;
	}, [filters.page, rowsData, selectedAlertDetails]);

	const detailsNextButtonDisabled = useMemo<boolean>(() => {
		if (selectedAlertDetails) {
			const totalCount = filters.ai_meta_data ? getAIEventsResponse?.count ?? 0 : getAlertEventsResponse?.count ?? 0;
			const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE);

			if (typeof filters.page === "number" && filters.page === totalPages) {
				const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.id === selectedAlertDetails.id);

				if (selectedAlertIndex === (totalCount % ROWS_PER_PAGE) - 1) {
					return true;
				}
			}
		}

		return false;
	}, [filters.page, filters.ai_meta_data, getAlertEventsResponse, getAIEventsResponse, rowsData, selectedAlertDetails]);

	function handleOpenAlertDetailsDialog(alertDetails: IVehicleLogRowDetails) {
		setSelectedAlertDetails(alertDetails);
	}

	function handleCloseAlertDetailsDialog(success?: boolean) {
		setSelectedAlertDetails(null);

		if (success) {
			refetchAlertEvents();
		}
	}

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleSubmitAiSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.ai_meta_data = searchInput.trim();
			else delete updatedFilters.ai_meta_data;

			return updatedFilters;
		});
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	function handleOpenTableSettingsDialog() {
		setShowTableSettingsDialog(true);
	}

	function handleCloseTableSettingsDialog() {
		setShowTableSettingsDialog(false);
		refetchUserSettings();
	}

	function handleChangeColumnVisibility(updatedValue: ColumnVisibilityType) {
		if (authState.user.username && getUserSettingsResponse) {
			const requestData: { [columnName: string]: boolean } = {};

			for (const [columnName, visibilityDetails] of Object.entries(updatedValue)) {
				requestData[columnName] = visibilityDetails.visible;
			}

			updateUserSettings({
				username: authState.user.username,
				data: {
					user_settings: {
						alerts: getUserSettingsResponse.user_settings.alerts ?? {},
						logs: requestData,
						traffic: getUserSettingsResponse.user_settings.traffic ?? {},
						challans: getUserSettingsResponse.user_settings.challans ?? {}
					}
				}
			});
		}
	}

	// function handleOpenTableFiltersDialog() {
	// 	setShowTableFiltersDialog(true);
	// }

	// function handleCloseTableFiltersDialog() {
	// 	setShowTableFiltersDialog(false);
	// 	setIsFilterApplied(false);
	// }

	function handleChangeToPreviousAlert(eventId: number) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === 0) {
			if (filters.page && filters.page > 1) {
				pageChanging.current = "previous";
				setFilters((currentFilters) => {
					const updatedFilters = { ...currentFilters };
					if (typeof updatedFilters.page === "number") {
						updatedFilters.page = updatedFilters.page - 1;
					}
					return updatedFilters;
				});
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex - 1];

		handleOpenAlertDetailsDialog(alertDetails);
	}

	function handleChangeToNextAlert(eventId: number) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === rowsData.length - 1) {
			const totalCount = filters.ai_meta_data ? getAIEventsResponse?.count ?? 0 : getAlertEventsResponse?.count ?? 0;
			const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE);

			if (filters.page && filters.page < totalPages) {
				pageChanging.current = "next";
				setFilters((currentFilters) => {
					const updatedFilters = { ...currentFilters };
					if (typeof updatedFilters.page === "number") {
						updatedFilters.page = updatedFilters.page + 1;
					}
					return updatedFilters;
				});
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex + 1];

		handleOpenAlertDetailsDialog(alertDetails);
	}

	function handleDownloadButtonClick() {
		startDownload({
			report_type: "alert_logs",
			report_format: "excel",
			job_meta_data: {
				filters: {
					start_date: filtersState.timeFilter.start_time,
					end_date: filtersState.timeFilter.end_time,
					product_type: EVENT_PRODUCT_TYPE_E.RECON,
					session: sessionId,
					camera: filters.camera,
					color: filters.color,
					device: filters.device,
					direction: filters.direction,
					radar_speed_range: filters.radar_speed_range,
					search: filters.search,
					speed_range: filters.speed_range,
					validated_status: filters.validated_status,
					vehicle_brand: filters.vehicle_brand,
					vehicle_category: filters.vehicle_category,
					vehicle_class: filters.vehicle_class,
					vehicle_model: filters.vehicle_model,
					vehicle_type: filters.vehicle_type,
					violation_ids: filters.violation_ids
				}
			}
		});
	}

	// function handleChangeSelectedDevices(deviceIds: number[]) {
	// 	setIsFilterApplied(true);
	// 	setFilters((currentFilters) => {
	// 		const updatedFilters = { ...currentFilters, page: 1 };

	// 		if (deviceIds.length === 0) delete updatedFilters.device;
	// 		else updatedFilters.device = deviceIds;

	// 		return updatedFilters;
	// 	});

	// 	handleCloseTableFiltersDialog();
	// }

	function handleToggleEventBookmark(eventId: string, isBookmarked: boolean) {
		setShowBookmarkLoaders((currentLoaders) => {
			const updatedLoaders = [...currentLoaders];

			if (!updatedLoaders.includes(eventId)) {
				updatedLoaders.push(eventId);
			}

			return updatedLoaders;
		});

		editEventBookmark({
			event_id: eventId,
			data: { is_bookmarked: isBookmarked }
		})
			.unwrap()
			.then(() => {
				setRowsData((currentRows) => {
					const updatedRows = [...currentRows];

					const index = updatedRows.findIndex((rowItem) => rowItem.event_id === eventId);
					if (index >= 0) {
						updatedRows[index].is_bookmarked = isBookmarked;
					}

					return updatedRows;
				});
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to bookmark vehicle log", "error");
			})
			.finally(() => {
				setShowBookmarkLoaders((currentLoaders) => {
					return currentLoaders.filter((item) => item !== eventId);
				});
			});
	}

	function handleToggleBookmarkFilter() {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };

			if (updatedFilters.is_bookmarked) delete updatedFilters.is_bookmarked;
			else updatedFilters.is_bookmarked = true;

			return updatedFilters;
		});
	}

	function handleOpenAISearchSettingsDialog() {
		setShowAISearchSettingsDialog(true);
	}

	function handleCloseAISearchSettingsDialog() {
		setShowAISearchSettingsDialog(false);
	}

	function handleSubmitAISearchSettings(values: IAISearchSettingsInputs) {
		setAiSearchFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };

			if (!isEqual(updatedFilters, values)) {
				delete updatedFilters.pagination_key;
				updatedFilters.page_number = 1;
			}

			updatedFilters.top_k = values.top_k;
			updatedFilters.accuracy = values.accuracy / 100;
			updatedFilters.n_probe = values.n_probe;

			return updatedFilters;
		});

		handleCloseAISearchSettingsDialog();
	}

	// RENDERER HELPER FUNCTIONS
	function handleChangeSelectedFilter(
		value: number,
		key: "vehicle_type" | "vehicle_category" | "color" | "vehicle_brand" | "vehicle_class" | "direction" | "camera"
	) {
		setIsFilterApplied(true);
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (value >= 0) updatedFilters[key] = value;
			else delete updatedFilters[key];

			return updatedFilters;
		});
	}

	function handleChangeSelectedStatus(value: ENFORCEMENT_VERIFY_STATUS_E | -1) {
		setIsFilterApplied(true);
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (value === -1) {
				delete updatedFilters.validated_status;
				delete updatedFilters.challan_status;
			} else {
				const status = statusMapping[value];

				if (status !== undefined) {
					updatedFilters.validated_status = status;
					delete updatedFilters.challan_status;
				} else {
					delete updatedFilters.validated_status;
					if (value === ENFORCEMENT_VERIFY_STATUS_E.CHALLAN_FAILED) updatedFilters.challan_status = "failed";
					else if (value === ENFORCEMENT_VERIFY_STATUS_E.CHALLAN_ISSUED) updatedFilters.challan_status = "issued";
				}
			}

			return updatedFilters;
		});
	}

	//
	function handleChangeRadarSpeedRange(index: number) {
		setIsFilterApplied(true);
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (index >= 0) updatedFilters.radar_speed_range = SPEED_RANGE_LIST[index].key;
			else delete updatedFilters.radar_speed_range;

			return updatedFilters;
		});
	}

	//
	// function handleChangeVisionSpeedRange(index: number) {
	// 	setIsFilterApplied(true);
	// 	setFilters((currentFilters) => {
	// 		const updatedFilters = { ...currentFilters, page: 1 };

	// 		if (index >= 0) updatedFilters.speed_range = SPEED_RANGE_LIST[index].key;
	// 		else delete updatedFilters.speed_range;

	// 		return updatedFilters;
	// 	});
	// }

	function handleSubmitSelectedViolations(selectedIds: number[]) {
		setIsFilterApplied(true);
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };

			if (!isEqual(updatedFilters.violation_ids, selectedIds)) {
				if (selectedIds.length === violationsList.length) {
					delete updatedFilters.violation_ids;
				} else {
					updatedFilters.violation_ids = selectedIds;
				}
			}

			return updatedFilters;
		});
	}

	function handleUpdateEventDetails(eventDetails: IGetAlertEventsResponseData) {
		if (eventDetails && eventDetails.id) {
			const updatedData = [...rowsData];
			const selectedLogIndex = updatedData.findIndex((logItem) => logItem.id === eventDetails.id);

			if (selectedLogIndex >= 0) {
				const vehicleType = vehicleTypesList?.find(
					(vehicleTypeItem) => vehicleTypeItem.id === eventDetails.vehicle_type
				);

				const violations: IVehicleLogViolationDetails[] = [];

				for (const violationItem of eventDetails.violations) {
					if (typeof violationItem.id === "number" && violationItem.violation_name) {
						violations.push({
							id: violationItem.id,
							violation_id: violationItem.violation ?? 0,
							name: NON_HSRP_VIOLATION_NAMES.includes(violationItem.violation_name)
								? "Non HSRP"
								: convertSnakeToTitleCase(violationItem.violation_name),
							lane_id: violationItem.lane,
							lane: violationItem.lane_name,
							is_speed_related: violationItem.violation_name.toLowerCase().includes("speed")
						});
					}
				}

				updatedData[selectedLogIndex] = {
					...updatedData[selectedLogIndex],
					icon: vehicleType?.icon ?? vehicleType?.icon_id ?? null,
					vehicle_type: eventDetails.vehicle_type ?? "",
					vehicle_number: eventDetails.number_plate,
					brand_icon: eventDetails.vehicle_brand_icon ?? "",
					color_icon: eventDetails.color_icon ?? "",
					speed: eventDetails.speed ? `${eventDetails.speed} km/hr` : "",
					radar_speed: eventDetails.radar_speed ? `${eventDetails.radar_speed} km/hr` : "",
					direction: eventDetails.direction,
					direction_name: eventDetails.direction_name,
					violations: violations,
					category_id: 0,
					crop_coordinates: eventDetails.crop_co_ordinates
						? {
								plate: eventDetails.crop_co_ordinates.plate,
								violation: eventDetails.crop_co_ordinates.violation?.[0] ?? [],
								...(eventDetails.crop_co_ordinates.plate_image_name != null && {
									plate_image_name: eventDetails.crop_co_ordinates.plate_image_name
								}),
								...(eventDetails.crop_co_ordinates.violation_image_name != null && {
									violation_image_name: eventDetails.crop_co_ordinates.violation_image_name
								})
						  }
						: { plate: [], violation: [] },
					number_plate: eventDetails.number_plate,
					status: eventDetails.validated_status,
					plate_type: eventDetails.vehicle_category,
					remarks: eventDetails.comment || "",
					is_challan_created: eventDetails.challan_info?.challans?.length > 0 || false,
					retry_challan: eventDetails.challan_info?.challans?.some((item) => item.status === "failed") || false,
					vehicle_images: eventDetails.new_evidence_images
				};

				setRowsData(updatedData);
				handleOpenAlertDetailsDialog(updatedData[selectedLogIndex]);
			}
		}
	}

	function handleChallanGenerated(eventId: number, challanGenerated: boolean, retryChallan: boolean) {
		if (!eventId) return;

		const updatedData = [...rowsData];
		const selectedLogIndex = updatedData.findIndex((logItem) => logItem.id === eventId);

		if (selectedLogIndex < 0) return;

		updatedData[selectedLogIndex] = {
			...updatedData[selectedLogIndex],
			is_challan_created: challanGenerated,
			retry_challan: retryChallan,
			challan_status: challanGenerated ? (retryChallan ? "failed" : "issued") : "pending"
		};

		setRowsData(updatedData);
		handleOpenAlertDetailsDialog(updatedData[selectedLogIndex]);
	}

	/** */
	/* function handleOpenVehicleModelDropdown() {
		setVehicleModelDropdownOpen(true);
	}

	function handleCloseVehicleModelDropdown() {
		setVehicleModelDropdownOpen(false);
	}

	function handleChangeSelectedVehicleModel(updatedValue: number) {
		setIsFilterApplied(true);
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.vehicle_model = updatedValue;
			else delete updatedFilters.vehicle_model;

			return updatedFilters;
		});

		handleCloseVehicleModelDropdown();
	} */

	/* const throttledModelAPICall = throttle((search: string) => setGetVehicleModelsFilters({ search: search }), 500, {
		leading: false
	}); */

	/* function renderVehicleModelDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="vehicle-model-dropdown"
					aria-controls="vehicle-model-dropdown-menu"
					aria-haspopup="true"
					aria-expanded={vehicleModelDropdownOpen}
					onClick={handleOpenVehicleModelDropdown}
					ref={vehicleModelDropdownAnchorRef}
				>
					<Typography variant="subtitle2" overflow="hidden" textOverflow="ellipsis">
						{typeof filters.vehicle_model === "number"
							? vehicleModelsList?.find((item) => item.id === filters.vehicle_model)?.name ?? "Model"
							: "Model"}
					</Typography>

					{vehicleModelDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<CustomAutocompletePopper
					id="vehicle-model-dropdown-menu"
					open={vehicleModelDropdownOpen}
					anchorEl={vehicleModelDropdownAnchorRef.current}
					placement="bottom-start"
				>
					<ClickAwayListener onClickAway={handleCloseVehicleModelDropdown}>
						<Autocomplete
							open
							disableCloseOnSelect
							onClose={(_event, reason) => {
								if (reason === "escape") {
									handleCloseVehicleModelDropdown();
								}
							}}
							value={vehicleModelsList ? vehicleModelsList.find((item) => item.id === filters.vehicle_model) : null}
							onChange={(_event, updatedValue) => handleChangeSelectedVehicleModel(updatedValue?.id ?? -1)}
							options={vehicleModelsList ?? []}
							getOptionKey={(item) => item.id}
							getOptionLabel={(item) => item.name}
							slotProps={{
								paper: { sx: { borderRadius: 0, borderBottomRightRadius: 6, borderBottomLeftRadius: 6 } }
							}}
							renderInput={(params) => (
								<CustomAutocompleteInput
									ref={params.InputProps.ref}
									inputProps={params.inputProps}
									autoFocus
									placeholder="Search"
									fullWidth
								/>
							)}
							filterOptions={(x) => x}
							onInputChange={(_event, value) => {
								// setModelFilterInput(value);
								throttledModelAPICall(value);
							}}
							loading={getVehicleModelsLoading || getVehicleModelsFetching}
						/>
					</ClickAwayListener>
				</CustomAutocompletePopper>
			</Box>
		);
	} */

	const columnsData: GridColDef[] = [
		{
			field: "icon",
			headerName: "",
			width: 75,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails, string>) => (
				<Box className="vehicle-log-icon-wrapper">
					<img src={params.row.vehicle_type_icon} alt={params.row.vehicle_type_name} />
				</Box>
			)
		},
		{
			field: "vehicle_type",
			headerName: "Type",
			flex: 1,
			minWidth: 110,
			sortable: false,
			valueGetter: (_value, row: IVehicleLogRowDetails) => row.vehicle_type_name,
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							minWidth={90}
							title="Type"
							menuItems={vehicleTypesList.map((item) => ({ id: item.id, label: item.name }))}
							onChange={(value) => handleChangeSelectedFilter(value, "vehicle_type")}
						/>
				  )
				: undefined
		},
		{
			field: "date",
			headerName: "Date",
			flex: 1,
			minWidth: 100,
			sortable: false,
			valueGetter: (_value, row: IVehicleLogRowDetails) => moment(row.timestamp).format("DD/MM/YYYY")
		},
		{
			field: "time",
			headerName: "Time",
			flex: 1,
			minWidth: 100,
			sortable: false,
			valueGetter: (_value, row: IVehicleLogRowDetails) => moment(row.timestamp).format("HH:mm:ss")
		},
		{
			field: "number_plate",
			headerName: "Number Plate",
			flex: 1,
			minWidth: 150,
			sortable: false
		},
		{
			field: "plate_image",
			headerName: "Plate Image",
			width: 160,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails>) => (
				<PlateImage
					eventId={params.row.plate_image_uuid}
					key={params.row.event_id}
					plateCrop={params.row.crop_coordinates?.plate}
					plateImageName={params.row.crop_coordinates?.plate_image_name}
					sourceImages={params.row.vehicle_images}
				/>
			)
		},
		{
			field: "plate_type",
			headerName: "Plate Type",
			flex: 1,
			minWidth: 150,
			sortable: false,
			valueFormatter: (value: string) => value.replace(/_/g, " "),
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							minWidth={130}
							title="Plate Type"
							menuItems={vehicleCategoriesList}
							onChange={(value) => handleChangeSelectedFilter(value, "vehicle_category")}
						/>
				  )
				: undefined
		},
		/* {
			field: "make",
			headerName: "Make",
			flex: 1,
			minWidth: 100,
			sortable: false,
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							minWidth={50}
							title="Make"
							menuItems={vehicleBrandsList}
							onChange={(value) => handleChangeSelectedFilter(value, "vehicle_brand")}
						/>
				  )
				: undefined,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails, string>) =>
				params.value ? (
					<Box className="vehicle-log-icon-wrapper">
						<Tooltip
							title={convertSnakeToTitleCase(params.value)}
							placement="top"
							slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -14] } }] } }}
						>
							<img src={params.row.make_icon} alt={params.value} height="24px" />
						</Tooltip>
					</Box>
				) : undefined
		}, */
		/* {
			field: "model",
			headerName: "Model",
			flex: 1,
			minWidth: 120,
			sortable: false,
			renderHeader: !filters.ai_meta_data ? renderVehicleModelDropdown : undefined,
			valueFormatter: (value: string) => value?.replace(/_/g, " ")
		}, */
		/* {
			field: "color",
			headerName: "Color",
			flex: 1,
			minWidth: 90,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails>) => (
				<Box className="vehicle-log-icon-wrapper">
					{params.row.color_icon ? (
						<Tooltip
							title={params.row.color}
							placement="top"
							slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -14] } }] } }}
						>
							<Box className="event-log-icon-wrapper border">
								<img src={params.row.color_icon} alt={params.row.color} height="100%" width="100%" />
							</Box>
						</Tooltip>
					) : null}
				</Box>
			),
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							minWidth={70}
							title="Color"
							menuItems={vehicleColorsList}
							onChange={(value) => handleChangeSelectedFilter(value, "color")}
						/>
				  )
				: undefined
		}, */
		// {
		// 	field: "speed_vision",
		// 	headerName: "Speed (V)",
		// 	flex: 1,
		// 	minWidth: 120,
		// 	sortable: false,
		// 	align: "center",
		// 	headerAlign: "center",
		// 	renderHeader: !filters.ai_meta_data
		// 		? () => (
		// 				<TableFilterDropdown
		// 					minWidth={100}
		// 					title="Speed (V)"
		// 					menuItems={SPEED_RANGE_LIST.map((item, index) => ({ id: index, label: item.label }))}
		// 					onChange={handleChangeVisionSpeedRange}
		// 				/>
		// 		  )
		// 		: undefined
		// },
		{
			field: "speed_radar",
			headerName: "Speed",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center",
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							minWidth={100}
							title="Speed"
							menuItems={SPEED_RANGE_LIST.map((item, index) => ({ id: index, label: item.label }))}
							onChange={handleChangeRadarSpeedRange}
						/>
				  )
				: undefined
		},
		{
			field: "violations",
			headerName: "Violations",
			flex: 1,
			minWidth: 160,
			sortable: false,
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							multiple
							title="Violations"
							onChange={handleSubmitSelectedViolations}
							menuItems={violationsList}
						/>
				  )
				: undefined,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
					{params.row.violations.map((violationItem, index) => (
						<Box key={`violation-${index}`} sx={{ flex: 1, display: "flex", alignItems: "center" }}>
							<Typography variant="body2">{violationItem.name}</Typography>
						</Box>
					))}
				</Box>
			)
		},
		// {
		// 	field: "class",
		// 	headerName: "Vehicle Class",
		// 	flex: 1,
		// 	minWidth: 150,
		// 	sortable: false,
		// 	// renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails>) => (
		// 	// 	<Box className="vehicle-log-icon-wrapper" sx={{ paddingX: "0.5rem", justifyContent: "flex-start" }}>
		// 	// 		<Box className="event-log-icon-wrapper">
		// 	// 			<img src={params.row.vehicle_class_icon} alt={params.value} height={24} />
		// 	// 		</Box>

		// 	// 		<Typography variant="body2">{params.value}</Typography>
		// 	// 	</Box>
		// 	// ),
		// 	renderHeader: !filters.ai_meta_data
		// 		? () => (
		// 				<TableFilterDropdown
		// 					minWidth={130}
		// 					title="Vehicle Class"
		// 					menuItems={vehicleClassesList}
		// 					onChange={(value) => handleChangeSelectedFilter(value, "vehicle_class")}
		// 				/>
		// 		  )
		// 		: undefined
		// },
		{
			field: "direction_name",
			headerName: "Direction",
			flex: 1,
			minWidth: 120,
			sortable: false,
			renderHeader: !filters.ai_meta_data
				? () => (
						<TableFilterDropdown
							title="Direction"
							menuItems={[
								{ id: EVENT_DIRECTION_E.APPROACHING, label: "Approaching" },
								{ id: EVENT_DIRECTION_E.DEPARTING, label: "Departing" }
							]}
							onChange={(value) => handleChangeSelectedFilter(value, "direction")}
							minWidth={100}
							capitalizeTitle
						/>
				  )
				: undefined,
			cellClassName: "text-transform-capitalize",
			valueFormatter: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "")
		},
		{
			field: "camera",
			headerName: "Camera",
			flex: 1,
			minWidth: 100,
			sortable: false,
			renderHeader: () => (
				<TableFilterDropdown
					title="Camera"
					menuItems={camerasList}
					onChange={(value) => handleChangeSelectedFilter(value, "camera")}
					minWidth={80}
				/>
			)
		},
		{
			field: "status",
			headerName: "Status",
			width: 130,
			sortable: false,
			renderHeader: () => (
				<TableFilterDropdown
					title="Status"
					menuItems={[
						{ id: ENFORCEMENT_VERIFY_STATUS_E.REJECT, label: "Rejected" },
						{ id: ENFORCEMENT_VERIFY_STATUS_E.PENDING, label: "Pending" },
						{ id: ENFORCEMENT_VERIFY_STATUS_E.VERIFIED, label: "Verified" },
						{ id: ENFORCEMENT_VERIFY_STATUS_E.CHALLAN_FAILED, label: "Challan Failed" },
						{ id: ENFORCEMENT_VERIFY_STATUS_E.CHALLAN_ISSUED, label: "Challan Issued" }
					]}
					onChange={handleChangeSelectedStatus}
				/>
			),
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails, EventValidationStatusType>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Box
						className={`log-status-indicator ${
							params.value === "approved"
								? params.row.challan_status === "failed"
									? "rejected"
									: "verified"
								: params.value === "reject"
								? "rejected"
								: "pending"
						}`}
					>
						<Typography variant="body2">
							{params.value === "approved"
								? params.row.challan_status === "failed"
									? "Challan Failed"
									: params.row.challan_status === "issued"
									? "Challan Issued"
									: "Verified"
								: params.value === "reject"
								? "Rejected"
								: "Pending"}
						</Typography>
					</Box>
				</Box>
			)
		},
		{
			field: "actions",
			headerName: "",
			width: 75,
			sortable: false,
			renderHeader: !filters.ai_meta_data
				? () => (
						<Box>
							<Tooltip title={filters.is_bookmarked ? "Show All Logs" : "Show Bookmarked Logs"} placement="top">
								<IconButton size="small" onClick={handleToggleBookmarkFilter}>
									{filters.is_bookmarked ? (
										<BookmarkIcon fontSize="inherit" htmlColor="white" />
									) : (
										<BookmarkBorderIcon fontSize="inherit" htmlColor="white" />
									)}
								</IconButton>
							</Tooltip>
						</Box>
				  )
				: undefined,
			renderCell: (params: GridRenderCellParams<IVehicleLogRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton
						color="primary"
						size="small"
						onClick={() => handleToggleEventBookmark(params.row.event_id, !params.row.is_bookmarked)}
						disabled={showBookmarkLoaders.includes(params.row.event_id)}
					>
						{showBookmarkLoaders.includes(params.row.event_id) ? (
							<CircularProgress size={14} />
						) : params.row.is_bookmarked ? (
							<BookmarkIcon fontSize="inherit" />
						) : (
							<BookmarkBorderIcon fontSize="inherit" />
						)}
					</IconButton>

					<IconButton color="primary" size="small" onClick={() => handleOpenAlertDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	useEffect(() => {
		let responseData: IGetAlertEventsResponseData[] = [];

		if (filters.ai_meta_data) {
			responseData = getAIEventsResponse?.results ?? [];
		} else {
			responseData = getAlertEventsResponse?.results ?? [];
		}

		const rows: IVehicleLogRowDetails[] = responseData.map((eventItem) => {
			const vehicleTypeDetails = vehicleTypesList.find(
				(vehicleTypeItem) => vehicleTypeItem.id === eventItem.vehicle_type
			);

			const isChallanCreated = eventItem.challan_info?.challans?.length > 0 || false;
			const retryChallan = eventItem.challan_info?.challans?.some((item) => item.status === "failed") || false;

			return {
				id: eventItem.id,
				timestamp: eventItem.timestamp,
				number_plate: eventItem.number_plate,
				location: "",
				speed_radar: eventItem.radar_speed ? `${eventItem.radar_speed} km/hr` : "",
				speed_vision: eventItem.speed ? `${eventItem.speed} km/hr` : "",
				plate_type: eventItem.vehicle_category ?? "",
				color: eventItem.color,
				color_icon: eventItem.color_icon,
				make: eventItem.vehicle_brand ?? "",
				make_icon: eventItem.vehicle_brand_icon ?? "",
				camera: eventItem.site_name,
				class: eventItem.vehicle_class ?? "",
				device: eventItem.device.device_name,
				violations: eventItem.violations
					.filter((violationItem) => !!violationItem.violation_name)
					.map((violationItem) => ({
						name: NON_HSRP_VIOLATION_NAMES.includes(violationItem.violation_name)
							? "Non HSRP"
							: convertSnakeToTitleCase(violationItem.violation_name),
						lane: violationItem.lane_name,
						id: violationItem.id,
						violation_id: violationItem.violation,
						lane_id: violationItem.lane,
						is_speed_related: violationItem.violation_name.toLowerCase().includes("speed")
					})),
				event_id: eventItem.unique_id,
				vehicle_type: eventItem.vehicle_type,
				vehicle_type_icon: vehicleTypeDetails?.icon ?? "",
				vehicle_class_icon: eventItem.vehicle_class_icon ?? "",
				direction: eventItem.direction,
				direction_name: eventItem.direction_name ?? "",
				model: eventItem.vehicle_model ?? "",
				is_bookmarked: eventItem.is_bookmarked,
				ai_meta_data: eventItem.ai_meta_data ?? "",
				bbox: eventItem.bbox ?? [],
				brand: "",
				brand_icon: eventItem.vehicle_brand_icon ?? "",
				brand_id: Number(eventItem.vehicle_brand),
				category: "",
				category_id: 0,
				color_id: Number(eventItem.color),
				crop_coordinates: eventItem.crop_co_ordinates
					? {
							plate: eventItem.crop_co_ordinates.plate,
							violation: eventItem.crop_co_ordinates.violation?.[0] ?? [],
							...(eventItem.crop_co_ordinates.plate_image_name != null && {
								plate_image_name: eventItem.crop_co_ordinates.plate_image_name
							}),
							...(eventItem.crop_co_ordinates.violation_image_name != null && {
								violation_image_name: eventItem.crop_co_ordinates.violation_image_name
							})
					  }
					: { plate: [], violation: [] },
				icon: vehicleTypeDetails?.icon ?? null,
				location_coordinates: eventItem.location_co_ordinates,
				location_name: eventItem.location_name,
				model_id: Number(eventItem.vehicle_model),
				officer: eventItem.officer_name,
				plate_image_uuid: eventItem.plate_evidences?.[0] || "",
				radar_speed: eventItem.radar_speed ? `${eventItem.radar_speed} km/hr` : "",
				site_id: Number(eventItem.camera.site_id),
				site_name: eventItem.camera.site_name,
				speed: eventItem.speed ? `${eventItem.speed} km/hr` : "",
				status: eventItem.validated_status,
				vehicle_images: eventItem.new_evidence_images,
				vehicle_image_frame_id: eventItem.evidence_video_url,
				vehicle_number: eventItem.number_plate,
				vehicle_type_name: vehicleTypeDetails?.name ?? "",
				vehicle_class: eventItem.vehicle_class ?? "",
				vehicle_class_id: 0,
				violation_image: "",
				lanes: eventItem.camera.camera_config_roi.map((laneItem) => ({ id: laneItem.id, name: laneItem.name })),
				image_width: eventItem.camera.width,
				image_height: eventItem.camera.height,
				remarks: eventItem.comment || "",
				is_challan_created: isChallanCreated,
				retry_challan: retryChallan,
				challan_status: isChallanCreated ? (retryChallan ? "failed" : "issued") : "pending"
			};
		});

		if (pageChanging.current === "previous") {
			handleOpenAlertDetailsDialog(rows[rows.length - 1]);
			pageChanging.current = null;
		} else if (pageChanging.current === "next") {
			handleOpenAlertDetailsDialog(rows[0]);
			pageChanging.current = null;
		}

		setRowsData(rows);
	}, [filters.ai_meta_data, getAIEventsResponse, getAlertEventsResponse, vehicleTypesList]);

	useEffect(() => {
		const columns: ColumnVisibilityType = {};

		if (columnVisibilityResponse) {
			for (const columnDetails of columnsData) {
				if (columnDetails.field === "actions") continue;

				columns[columnDetails.field] = {
					visible: columnVisibilityResponse[columnDetails.field] ?? true,
					disabled: disabledColumns.includes(columnDetails.field),
					name: columnDetails.headerName || columnDetails.field
				};
			}
		}

		setColumnVisibility(columns);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnVisibilityResponse]);

	// useEffect(() => {
	// 	if (filtersState.timeFilter) {
	// 		if (filtersState.timeRange !== TIME_RANGE_OPTIONS_E.TODAY) {
	// 			setIsFilterApplied(true);
	// 		} else {
	// 			setIsFilterApplied(false);
	// 		}

	// 		setFilters((currentFilters) => ({
	// 			...currentFilters,
	// 			page: 1,
	// 			timestamp__gte: filtersState.timeFilter.start_time,
	// 			timestamp__lte: filtersState.timeFilter.end_time
	// 		}));
	// 	}
	// }, [filtersState]);

	useEffect(() => {
		if (updateUserSettingsError) {
			notify(
				"data" in updateUserSettingsError && updateUserSettingsError.data
					? String(updateUserSettingsError.data)
					: "Failed to save table settings",
				"error"
			);

			resetUpdateUserSettings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUserSettingsError]);

	useEffect(() => {
		if (updateUserSettingsSuccess) {
			handleCloseTableSettingsDialog();
			resetUpdateUserSettings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUserSettingsSuccess]);

	useEffect(() => {
		setAiSearchFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters };

			if (updatedFilters.page_number !== filters.page) {
				updatedFilters.page_number = filters.page;
				updatedFilters.pagination_key = getAIEventsResponse?.pagination_key;
			}

			if (updatedFilters.search !== filters.ai_meta_data) {
				updatedFilters.search = filters.ai_meta_data ?? "";
				delete updatedFilters.pagination_key;
			}

			// if (updatedFilters.timestamp__gte !== filters.timestamp__gte) {
			// 	updatedFilters.timestamp__gte = filters.timestamp__gte;
			// 	delete updatedFilters.pagination_key;
			// }

			// if (updatedFilters.timestamp__lte !== filters.timestamp__lte) {
			// 	updatedFilters.timestamp__lte = filters.timestamp__lte;
			// 	delete updatedFilters.pagination_key;
			// }

			if (!isEqual(updatedFilters.device_id, filters.device)) {
				updatedFilters.device_id = filters.device;
				delete updatedFilters.pagination_key;
			}

			if (updatedFilters.camera_id !== filters.camera) {
				updatedFilters.camera_id = filters.camera;
				delete updatedFilters.pagination_key;
			}

			return updatedFilters;
		});
	}, [
		filters.ai_meta_data,
		filters.camera,
		filters.device,
		filters.page,
		// filters.timestamp__gte,
		// filters.timestamp__lte,
		getAIEventsResponse?.pagination_key
	]);

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<Box className="page-header-actions-wrapper">
					<Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
						{!isLive ? (
							<Button
								variant="text"
								startIcon={<ArrowBackIcon />}
								onClick={navigateBack}
								sx={{ textTransform: "none" }}
							>
								Back to Sessions List
							</Button>
						) : null}
					</Box>
				</Box>

				<Box className="page-header-actions-wrapper">
					{authState.user.enable_ai_search ? (
						<>
							{filters.ai_meta_data ? (
								<IconButton color="primary" onClick={handleOpenAISearchSettingsDialog}>
									<Badge color="secondary" variant="dot">
										<TuneIcon />
									</Badge>
								</IconButton>
							) : null}

							<TableSearchInput
								aiSearch
								showClearButton={!!filters.ai_meta_data}
								placeholder="AI Search"
								onSubmit={handleSubmitAiSearchInput}
							/>
						</>
					) : !filters.ai_meta_data ? (
						<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />
					) : null}

					<Button variant="outlined" color="primary" onClick={handleOpenTableSettingsDialog}>
						Table Settings
					</Button>

					{/* <IconButton color="primary" onClick={handleOpenTableFiltersDialog}>
						<FilterAltIcon />
					</IconButton> */}

					<IconButton color="primary" onClick={handleDownloadButtonClick} disabled={showDownloadButtonLoader}>
						{showDownloadButtonLoader ? <CircularProgress size={22} /> : <DownloadIcon />}
					</IconButton>
				</Box>
			</Box>

			<Box className="page-content">
				<AGDataGrid
					darkTheme
					columns={columnsData}
					rows={rowsData}
					paginationMode="server"
					paginationModel={{
						page: filters.ai_meta_data ? aiSearchFilters.page_number - 1 : filters.page - 1,
						pageSize: ROWS_PER_PAGE
					}}
					onPaginationModelChange={handlePageChange}
					rowCount={filters.ai_meta_data ? getAIEventsResponse?.count ?? 0 : getAlertEventsResponse?.count ?? 0}
					loading={
						filters.ai_meta_data
							? getAIEventsLoading || getAIEventsFetching
							: getAlertEventsLoading || getAlertEventsFetching
					}
					hideFooter={
						(filters.ai_meta_data ? getAIEventsResponse?.count ?? 0 : getAlertEventsResponse?.count ?? 0) <=
						ROWS_PER_PAGE
					}
					getRowHeight={({ densityFactor, model }) => {
						if (model.violations && model.violations.length > 1) {
							return 50 * model.violations.length * densityFactor;
						}

						return null;
					}}
					columnVisibilityModel={columnVisibilityModel}
				/>
			</Box>

			<TableSettingsDialog
				open={showTableSettingsDialog}
				columnVisibility={columnVisibility}
				saveButtonLoading={updateUserSettingsLoading}
				onChangeColumnVisibility={handleChangeColumnVisibility}
				onClose={handleCloseTableSettingsDialog}
			/>

			{/* <TableFilterDialog
				open={showTableFiltersDialog}
				defaultValue={filters.device}
				productType={EVENT_PRODUCT_TYPE_E.RECON}
				onClose={handleCloseTableFiltersDialog}
				onChange={handleChangeSelectedDevices}
			/> */}

			<VehicleLogDetailsDialog
				open={!!selectedAlertDetails}
				vehicleLogDetails={selectedAlertDetails}
				previousButtonDisabled={detailsPreviousButtonDisabled}
				nextButtonDisabled={detailsNextButtonDisabled}
				pageChangeLoading={!!pageChanging.current && (getAlertEventsLoading || getAlertEventsFetching)}
				onClose={handleCloseAlertDetailsDialog}
				onPrevious={handleChangeToPreviousAlert}
				onNext={handleChangeToNextAlert}
				onUpdated={handleUpdateEventDetails}
				onChallanGenerated={handleChallanGenerated}
			/>

			<AISearchSettingsDialog
				open={showAISearchSettingsDialog}
				filters={{
					top_k: aiSearchFilters.top_k,
					accuracy: aiSearchFilters.accuracy,
					n_probe: aiSearchFilters.n_probe
				}}
				onClose={handleCloseAISearchSettingsDialog}
				onSubmit={handleSubmitAISearchSettings}
			/>
		</Box>
	);
};

export default VehicleLogs;
